import { computed, type MaybeRefOrGetter, ref, toRef } from 'vue'
import usePagination from '@/composables/usePagination'
import { orderBy } from 'lodash'

export interface SortValue {
  key: string
  order: 'desc' | 'asc'
}

export interface SortData<T> {
  key: string | string[] | ((item: T) => string)
  order: 'desc' | 'asc'
}

function usePaginatedTable<T>(totalNumberOfItems: MaybeRefOrGetter<number>) {
  const totalItems = toRef(totalNumberOfItems)
  const {
    itemsPerPage,
    currentPage,
    nextPage,
    prevPage,
    setItemsPerPage,
    setCurrentPage,
    totalPages
  } = usePagination(totalItems)

  const sortBy = ref<SortData<T>[]>([])
  const tableData = ref<T[]>([])

  const numberOfAlreadyFetchedItems = computed(() => tableData.value.length)

  const sortedData = computed<T[]>(() => {
    return orderBy(tableData.value, sortBy.value?.[0]?.key, sortBy.value?.[0]?.order) as T[]
  })

  const paginatedData = computed<T[]>(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value
    const end = start + itemsPerPage.value

    return sortedData.value.slice(start, end) as T[]
  })

  function handleItemsPerPageChange(value: number) {
    setCurrentPage(1)
    setItemsPerPage(value)
  }

  function clearTableData() {
    tableData.value = []
  }

  function addTableData(data: T[]) {
    tableData.value.push(...(data as any))
  }

  function setSortBy(value: SortData<T>[]) {
    sortBy.value = value
  }

  function setTableData(data: T[]) {
    tableData.value = data
  }

  return {
    paginatedData,
    itemsPerPage,
    currentPage,
    totalItems,
    totalPages,
    numberOfAlreadyFetchedItems,
    sortBy,
    addTableData,
    setTableData,
    clearTableData,
    nextPage,
    prevPage,
    handleItemsPerPageChange,
    setCurrentPage,
    setSortBy
  }
}

export default usePaginatedTable
